/** Конфиг боковой панели для каждого раздела (sub-navigation).
 *  Используется компонентом Sidebar внутри WorkspaceShell. */
import type { Component } from "solid-js";
import {
  Activity,
  BarChart3,
  Building,
  Calendar,
  LayoutGrid,
  Handshake,
  Inbox,
  Link as LinkIcon,
  ListOrdered,
  Package,
  Shield,
  TrendingDown,
  TrendingUp,
  Truck,
  Users,
  Warehouse,
  Waypoints,
} from "lucide-solid";

export type SectionKey = "monitoring" | "planning" | "configuration" | "system";

export interface SubItem {
  /** Относительный сегмент пути внутри раздела, напр. `counterparties`. */
  key: string;
  /** Полный путь до подраздела, напр. `/system/counterparties`. */
  path: string;
  /** Ключ перевода — i18n-словарь возвращает локализованный текст. */
  labelKey: string;
  Icon: Component<{ size?: number; class?: string; "stroke-width"?: number }>;
}

const item = (
  section: SectionKey,
  key: string,
  labelKey: string,
  Icon: SubItem["Icon"],
): SubItem => ({ key, labelKey, Icon, path: `/${section}/${key}` });

export const SIDEBAR_BY_SECTION: Record<SectionKey, SubItem[]> = {
  monitoring: [
    item("monitoring", "processing", "sidebar.monitoring.processing", Activity),
    item("monitoring", "transport", "sidebar.monitoring.transport", Truck),
    item("monitoring", "cargo", "sidebar.monitoring.cargo", Package),
    item("monitoring", "stats", "sidebar.monitoring.stats", BarChart3),
    item("monitoring", "messages-queue", "sidebar.monitoring.messagesQueue", Inbox),
  ],
  planning: [
    item("planning", "timeslots", "sidebar.planning.timeslots", Calendar),
    item("planning", "shipping", "sidebar.planning.shipping", TrendingUp),
    item("planning", "supply", "sidebar.planning.supply", TrendingDown),
  ],
  configuration: [
    item("configuration", "my-data", "sidebar.configuration.myData", Building),
    item("configuration", "composition", "sidebar.configuration.composition", LayoutGrid),
    item("configuration", "queues", "sidebar.configuration.queues", ListOrdered),
    item("configuration", "directions", "sidebar.configuration.directions", Waypoints),
  ],
  system: [
    item("system", "objects", "sidebar.system.objects", Warehouse),
    item("system", "counterparties", "sidebar.system.counterparties", Handshake),
    item("system", "users", "sidebar.system.users", Users),
    item("system", "integrations", "sidebar.system.integrations", LinkIcon),
    item("system", "administration", "sidebar.system.administration", Shield),
  ],
};

/** Достать ключ секции из pathname (например `/monitoring/...` → `monitoring`). */
export function sectionFromPath(pathname: string): SectionKey | null {
  const first = pathname.split("/").filter(Boolean)[0] ?? "";
  if (first in SIDEBAR_BY_SECTION) return first as SectionKey;
  return null;
}

/** Дефолтный путь подраздела секции — первый в списке. */
export function defaultSubPath(section: SectionKey): string {
  return SIDEBAR_BY_SECTION[section][0]?.path ?? `/${section}`;
}

/** Дефолтный ключ подраздела секции — первый в списке. */
export function defaultSub(section: SectionKey): string {
  return SIDEBAR_BY_SECTION[section][0]?.key ?? "";
}
